import React, { useContext, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";

import {
  FiPlus,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiMessageSquare,
} from "react-icons/fi";

import Button from "../../components/Button.jsx";
import { ticketApis } from "../../apis/profile.apis";
import LoaderContext from "../../components/loader/LoaderContext.js";
import { useSelector } from "react-redux";

const Complaints = () => {
   const { handleLoading } = useContext(LoaderContext);
    const { loading } = useSelector((state) => state.store);
  const [tickets, setTickets] = useState([]);

  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    problem_type: "",
    subject: "",
    description: "",
    priority: "MEDIUM",
  });

  // ================= FETCH TICKETS =================

  const fetchTickets = async () => {
    try {
      handleLoading(true);

      const res = await ticketApis.getAllTickets({
        page: 1,
        limit: 20,
      });

      setTickets(res?.data?.data?.tickets || []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch complaints");
    } finally {
      handleLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // ================= CREATE COMPLAINT =================

  const handleSubmitComplaint = async () => {
    try {
      if (
        !formData.problem_type ||
        !formData.subject ||
        !formData.description
      ) {
        return toast.error("Please fill all required fields");
      }

      await ticketApis.createTicket(formData);

      toast.success("Complaint raised successfully");

      setOpenModal(false);

      setFormData({
        problem_type: "",
        subject: "",
        description: "",
        priority: "MEDIUM",
      });

      fetchTickets();
    } catch (error) {
      console.log(error);
      toast.error("Failed to create complaint");
    }
  };

  // ================= STATUS STYLE =================

  const getStatusStyle = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-yellow100 text-yellow700 border border-yellow300";

      case "IN_PROGRESS":
        return "bg-blue100 text-blue700 border border-blue300";

      case "RESOLVED":
        return "bg-green100 text-green700 border border-green300";

      case "CLOSED":
        return "bg-red100 text-red700 border border-red300";

      default:
        return "bg-gray100 text-gray700 border border-gray300";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OPEN":
        return <FiAlertCircle size={15} />;

      case "IN_PROGRESS":
        return <FiClock size={15} />;

      case "RESOLVED":
        return <FiCheckCircle size={15} />;

      default:
        return <FiMessageSquare size={15} />;
    }
  };

  const problemOptions = [
    {
      label: "Order Issue",
      value: "ORDER_ISSUE",
    },
    {
      label: "Payment Issue",
      value: "PAYMENT_ISSUE",
    },
    {
      label: "Delivery Issue",
      value: "DELIVERY_ISSUE",
    },
    {
      label: "Product Issue",
      value: "PRODUCT_ISSUE",
    },
    {
      label: "Refund Request",
      value: "REFUND_REQUEST",
    },
    {
      label: "Technical Issue",
      value: "TECHNICAL_ISSUE",
    },
    {
      label: "Other",
      value: "OTHER",
    },
  ];

  const priorityOptions = [
    {
      label: "Low",
      value: "LOW",
    },
    {
      label: "Medium",
      value: "MEDIUM",
    },
    {
      label: "High",
      value: "HIGH",
    },
    {
      label: "Urgent",
      value: "URGENT",
    },
  ];

  return (
    <div className="p-5">
      {/* ================= HEADER ================= */}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Complaints & Support
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Raise complaints and track admin responses
          </p>
        </div>

        <button
          onClick={() => setOpenModal(true)}
          className="flex items-center gap-2 bg-green500 hover:bg-green700 text-white px-5 py-3 rounded-xl transition-all"
        >
          <FiPlus size={18} />
          Raise Complaint
        </button>
      </div>

      {/* ================= TICKETS ================= */}

      <div className="grid grid-cols-1 gap-5">
        {tickets?.map((ticket) => (
          <div
            key={ticket?._id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
          >
            {/* TOP */}

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-lg font-bold text-gray-800">
                    {ticket?.subject}
                  </h3>

                  <span
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                      ticket?.status
                    )}`}
                  >
                    {getStatusIcon(ticket?.status)}
                    {ticket?.status}
                  </span>
                </div>

                <p className="text-sm text-gray-500 mt-2">
                  Ticket ID : {ticket?.ticket_id}
                </p>
              </div>

              <div className="text-sm text-gray-500">
                {new Date(ticket?.createdAt).toLocaleDateString()}
              </div>
            </div>

            {/* DETAILS */}

            <div className="mt-5">
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium">
                  {ticket?.problem_type}
                </span>

                <span className="bg-red-100 text-red-700 px-3 py-1 rounded-lg text-xs font-medium">
                  Priority : {ticket?.priority}
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-sm text-gray-700 leading-6">
                  {ticket?.description}
                </p>
              </div>
            </div>

            {/* REPLIES */}

            {ticket?.replies?.length > 0 && (
              <div className="mt-5">
                <h4 className="font-semibold text-gray800 mb-3">
                  Admin Replies
                </h4>

                <div className="space-y-3">
                  {ticket?.replies?.map((reply, index) => (
                    <div
                      key={index}
                      className="bg-green50 border border-green100 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-green700">
                          {reply?.role || "Admin"}
                        </span>

                        <span className="text-xs text-gray-500">
                          {new Date(reply?.createdAt).toLocaleString()}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 leading-6">
                        {reply?.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {!loading && tickets?.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center">
            <FiMessageSquare
              size={45}
              className="mx-auto text-gray-400 mb-4"
            />

            <h3 className="text-lg font-semibold text-gray-700">
              No Complaints Found
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              You have not raised any complaints yet
            </p>
          </div>
        )}
      </div>

      {/* ================= CREATE MODAL ================= */}

      <Dialog
        visible={openModal}
        onHide={() => setOpenModal(false)}
        style={{ width: "650px" }}
        draggable={false}
        resizable={false}
        showHeader={false}
        className="rounded-3xl overflow-hidden"
      >
        <div className="bg-white rounded-3xl overflow-hidden">
          {/* HEADER */}

          <div className="bg-green500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <FiMessageSquare size={24} />
              </div>

              <div>
                <h3 className="text-2xl font-bold">
                  Raise Complaint
                </h3>

                <p className="text-sm text-red-100 mt-1">
                  Submit your issue to admin support
                </p>
              </div>
            </div>
          </div>

          {/* BODY */}

          <div className="p-6 space-y-5">
            {/* PROBLEM TYPE */}

            <div>
              <label className="block text-sm font-semibold text-gray700 mb-2">
                Problem Type
              </label>

              <Dropdown
                value={formData.problem_type}
                options={problemOptions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    problem_type: e.value,
                  })
                }
                placeholder="Select problem type"
                className="w-full border"
              />
            </div>

            {/* PRIORITY */}

            <div>
              <label className="block text-sm font-semibold text-gray700 mb-2">
                Priority
              </label>

              <Dropdown
                value={formData.priority}
                options={priorityOptions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    priority: e.value,
                  })
                }
                placeholder="Select priority"
                className="w-full"
              />
            </div>

            {/* SUBJECT */}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subject
              </label>

              <input
                type="text"
                placeholder="Enter complaint subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    subject: e.target.value,
                  })
                }
                className="w-full border border-gray300 rounded-xl px-4 py-3 outline-none focus:border-green500"
              />
            </div>

            {/* DESCRIPTION */}

            <div>
              <label className="block text-sm font-semibold text-gray700 mb-2">
                Description
              </label>

              <InputTextarea
                rows={6}
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
                placeholder="Describe your issue..."
                className="w-full rounded-xl border border-gray300 p-4"
                style={{
                  resize: "none",
                }}
              />
            </div>

            {/* BUTTONS */}

            <div className="flex justify-end gap-3 pt-3">
              <Button
                text="Cancel"
                outlined
                onClick={() => setOpenModal(false)}
              />

              <Button
                text="Submit Complaint"
                onClick={handleSubmitComplaint}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Complaints; 
